/*
  Gut landing (gl-) behaviors for the homepage sections rebuilt on muves.co's
  structure. Plain custom elements, no dependencies:

    gl-marquee        infinite strip (fills the viewport, 4 copies, -25% loop)
    gl-slider         transform carousel (drag, snap, dots, arrows, autoplay)
    gl-accordion      expand/collapse rows
    gl-gallery        product gallery: slider + thumbnails + nutrition dialog
    gl-cycle          GIF-style image rotation
    gl-fit-row        keeps a row of labels on one line at one shared size
    gl-buybox         homepage buy box: options × purchase type → cart

  Every element guards its own definition, so the script can be loaded more
  than once without double-registering.
*/
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 768px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Keyboard mode: html.gl-kbd is set while the visitor is navigating with
     the keyboard and cleared on any pointer press. gl-btn draws its focus
     ring only in keyboard mode, so focus moved by a script (Klaviyo hands
     focus back to the first button when its popup closes) doesn't leave a
     ring on the button. */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key.indexOf('Arrow') === 0) {
      document.documentElement.classList.add('gl-kbd');
    }
  }, true);
  document.addEventListener('pointerdown', function () {
    document.documentElement.classList.remove('gl-kbd');
  }, true);

  /* Iframes written with data-gl-src get their src only once they're on
     screen (or within 200px of it). Carousels of embeds — plus the copies a
     looping slider makes — would otherwise request every player at once,
     which TikTok answers with "Access Denied". */
  var deferObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var frame = entry.target;
          frame.src = frame.getAttribute('data-gl-src');
          frame.removeAttribute('data-gl-src');
          deferObserver.unobserve(frame);
        });
      }, { rootMargin: '200px' })
    : null;

  function deferFrames(root) {
    (root || document).querySelectorAll('iframe[data-gl-src]').forEach(function (frame) {
      if (deferObserver) deferObserver.observe(frame);
      else frame.src = frame.getAttribute('data-gl-src');
    });
  }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  /* ────────────────────────────────────────────────────────────────────
     gl-marquee — Muves' marquee-section: repeat the authored items until one
     list is at least a viewport wide, then append three clones of that list
     so a -25% translate loops seamlessly. With data-speed-mobile /
     data-speed-desktop (px per second) the loop duration is derived from the
     list's width, so the strip moves at the same pace however many items it
     holds or however wide the screen is.
     ──────────────────────────────────────────────────────────────────── */
  define('gl-marquee', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('[data-marquee-track]');
      this.content = this.querySelector('[data-marquee-content]');
      if (!this.track || !this.content) return;
      this.originals = Array.from(this.content.children);
      this.build = this.build.bind(this);
      this.build();
      this._onResize = debounce(this.build, 150);
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._init = false;
    }

    build() {
      this.track.querySelectorAll('[data-marquee-dup]').forEach(function (n) { n.remove(); });
      this.content.querySelectorAll('[data-marquee-dup]').forEach(function (n) { n.remove(); });
      if (REDUCED.matches || !this.originals.length) return;

      var guard = 0;
      while (this.content.scrollWidth < window.innerWidth && guard < 20) {
        this.originals.forEach(function (item) {
          var clone = item.cloneNode(true);
          clone.setAttribute('data-marquee-dup', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          this.content.appendChild(clone);
        }, this);
        guard++;
      }

      for (var i = 0; i < 3; i++) {
        var copy = this.content.cloneNode(true);
        copy.setAttribute('data-marquee-dup', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('aria-label');
        copy.querySelectorAll('[data-shopify-editor-block]').forEach(function (n) {
          n.removeAttribute('data-shopify-editor-block');
        });
        this.track.appendChild(copy);
      }

      var speed = parseFloat(DESKTOP.matches ? this.dataset.speedDesktop : this.dataset.speedMobile);
      if (speed > 0) {
        this.style.setProperty('--gl-mq-duration', (this.content.scrollWidth / speed).toFixed(2) + 's');
      }
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-slider — the Embla behaviors Muves uses, on a translated flex track.

     Attributes:
       data-align="start|center"   snap alignment (default start)
       data-autoplay="3000"        ms between auto-advances (0/absent = off)
       data-pause-on-hover         pause autoplay while hovered
       data-loop                   infinite loop (Embla loop: true)
       data-mobile-only            inactive at ≥768px (CSS lays it out)

     Autoplay follows Embla's autoplay plugin as Muves configures it: it
     resumes after a drag (stopOnInteraction: false), pauses on hover when
     asked, and pauses while the tab is hidden. It stops for good once someone
     interacts with an embedded video (focus moves into an iframe inside the
     slider), so it can't slide away mid-play.

     Children: [data-gl-viewport] > [data-gl-track] > slides, plus optional
     [data-gl-prev], [data-gl-next], [data-gl-dots]. Without data-loop, snaps
     are trimmed to the scrollable range (Embla's containScroll: 'trimSnaps').
     With it, the slide set is cloned once before and once after the
     originals; the track always travels forward and, after each move that
     lands in a clone, jumps invisibly back to the matching original.

     Dispatches `gl:select` ({ detail: { index } }) when the slide changes.
     ──────────────────────────────────────────────────────────────────── */
  define('gl-slider', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.viewport = this.querySelector('[data-gl-viewport]');
      this.track = this.querySelector('[data-gl-track]');
      if (!this.viewport || !this.track) return;

      this.index = 0;
      this.offset = 0;
      this.align = this.dataset.align === 'center' ? 'center' : 'start';
      this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10) || 0;
      this.mobileOnly = this.hasAttribute('data-mobile-only');
      this.loop = this.hasAttribute('data-loop');
      this.hovering = false;
      this.visible = true;

      this.prevBtn = this.querySelector('[data-gl-prev]');
      this.nextBtn = this.querySelector('[data-gl-next]');
      this.dotsEl = this.querySelector('[data-gl-dots]');

      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.goTo(self.index - 1); self.restart(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.goTo(self.index + 1); self.restart(); });

      if (this.hasAttribute('data-pause-on-hover')) {
        this.addEventListener('mouseenter', function () { self.hovering = true; self.stop(); });
        this.addEventListener('mouseleave', function () { self.hovering = false; self.start(); });
      }

      this.bindDrag();

      this.track.addEventListener('transitionend', function (e) {
        if (e.target === self.track) self.normalize();
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) self.stop(); else self.start();
      });

      // Clicking into a cross-origin iframe (a TikTok player) blurs the
      // window and moves focus to the iframe — the only signal we get.
      window.addEventListener('blur', function () {
        setTimeout(function () {
          var el = document.activeElement;
          if (el && el.tagName === 'IFRAME' && self.contains(el)) {
            self.userPaused = true;
            self.stop();
          }
        }, 0);
      });
      this._onResize = debounce(function () { self.refresh(); }, 120);
      window.addEventListener('resize', this._onResize);
      this._onMedia = function () { self.refresh(); };
      DESKTOP.addEventListener('change', this._onMedia);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          self.visible = entries[0].isIntersecting;
          if (self.visible) self.start(); else self.stop();
        }, { threshold: 0.2 });
        this.io.observe(this);
      }

      this.refresh();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this._onResize);
      DESKTOP.removeEventListener('change', this._onMedia);
      if (this.io) this.io.disconnect();
      this._init = false;
    }

    get active() {
      return !(this.mobileOnly && DESKTOP.matches);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    /* Clone the slide set before and after the originals (loop mode). */
    buildLoop() {
      var originals = this.slides;
      this.count = originals.length;
      if (this.count < 2) { this.loop = false; return; }
      var self = this;
      function clone(el) {
        var c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-gl-clone', '');
        c.removeAttribute('data-shopify-editor-block');
        c.querySelectorAll('a, button, iframe, input, select, textarea').forEach(function (n) {
          n.setAttribute('tabindex', '-1');
        });
        return c;
      }
      var before = document.createDocumentFragment();
      originals.forEach(function (el) { before.appendChild(clone(el)); });
      originals.forEach(function (el) { self.track.appendChild(clone(el)); });
      this.track.insertBefore(before, this.track.firstChild);
      this.index = this.count;
      this._looped = true;
      deferFrames(this.track);
    }

    /* After a move lands in a clone set, jump to the same slide in the
       original set without animating. */
    normalize() {
      if (!this.loop || !this._looped || this.dragging) return;
      var n = this.count;
      if (this.index < n) this.index += n;
      else if (this.index >= 2 * n) this.index -= n;
      else return;
      this.apply(false);
    }

    /* Rebuild snap points from current layout. Call after slides change. */
    refresh() {
      if (!this.active) {
        this.track.style.transform = '';
        this.snaps = [0];
        this.renderDots();
        return;
      }
      if (this.loop && !this._looped) this.buildLoop();
      var vw = this.viewport.clientWidth;
      var base = this.track.offsetLeft;
      var last = this.slides[this.slides.length - 1];
      var contentEnd = last ? last.offsetLeft + last.offsetWidth - base : 0;
      var max = Math.max(0, contentEnd - vw);
      var raw = this.slides.map(function (s) {
        var left = s.offsetLeft - base;
        return this.align === 'center' ? left + s.offsetWidth / 2 - vw / 2 : left;
      }, this);
      if (this.loop) {
        this.snaps = raw.map(Math.round);
        this.renderDots();
        this.apply(false);
        return;
      }
      var snaps = [];
      raw.forEach(function (x) {
        var c = Math.round(Math.min(Math.max(x, 0), max));
        if (!snaps.length || Math.abs(snaps[snaps.length - 1] - c) > 1) snaps.push(c);
      });
      this.snaps = snaps.length ? snaps : [0];
      this.index = Math.min(this.index, this.snaps.length - 1);
      this.renderDots();
      this.apply(false);
    }

    renderDots() {
      if (!this.dotsEl) return;
      var self = this;
      var count = !this.active ? 0 : this.loop ? this.count : this.snaps.length;
      var current = this.loop ? this.index % this.count : this.index;
      if (this.dotsEl.children.length !== count) {
        this.dotsEl.innerHTML = '';
        for (var i = 0; i < count; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          (function (n) { b.addEventListener('click', function () { self.goTo(self.loop ? self.count + n : n); self.restart(); }); })(i);
          this.dotsEl.appendChild(b);
        }
      }
      Array.from(this.dotsEl.children).forEach(function (b, i) {
        b.setAttribute('aria-current', String(i === current));
      });
    }

    apply(animate) {
      if (!this.active) return;
      this.offset = this.snaps[this.index] || 0;
      if (animate === false) this.track.classList.add('is-dragging');
      this.track.style.transform = 'translate3d(' + (-this.offset) + 'px,0,0)';
      if (animate === false) {
        void this.track.offsetWidth;
        this.track.classList.remove('is-dragging');
      }
      if (this.prevBtn) this.prevBtn.disabled = !this.loop && this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && this.index >= this.snaps.length - 1;
    }

    goTo(i) {
      if (!this.active) return;
      var n = this.snaps.length;
      var next = Math.max(0, Math.min(i, n - 1));
      var changed = next !== this.index;
      this.index = next;
      this.apply(true);
      this.renderDots();
      if (this.loop) {
        // transitionend normally triggers this; the timer covers cases where
        // it never fires (reduced motion, a backgrounded tab).
        var self = this;
        clearTimeout(this._normalizeTimer);
        this._normalizeTimer = setTimeout(function () { self.normalize(); }, 520);
      }
      if (changed) {
        var logical = this.loop ? this.index % this.count : this.index;
        this.dispatchEvent(new CustomEvent('gl:select', { detail: { index: logical } }));
      }
    }

    start() {
      this.stop();
      if (!this.autoplayMs || this.userPaused || REDUCED.matches || !this.visible || this.hovering || !this.active) return;
      var self = this;
      this.timer = setInterval(function () {
        if (self.dragging) return;
        if (self.loop) self.goTo(self.index + 1);
        else self.goTo(self.index >= self.snaps.length - 1 ? 0 : self.index + 1);
      }, this.autoplayMs);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    bindDrag() {
      var self = this;
      var startX = 0, startY = 0, startOffset = 0, lastX = 0, lastT = 0, velocity = 0, axis = null, pid = null;

      this.viewport.addEventListener('pointerdown', function (e) {
        if (!self.active || (e.pointerType === 'mouse' && e.button !== 0)) return;
        pid = e.pointerId;
        if (self.loop) self.normalize();
        startX = lastX = e.clientX;
        startY = e.clientY;
        startOffset = self.offset;
        lastT = performance.now();
        velocity = 0;
        axis = null;
        self.dragging = true;
      });

      this.viewport.addEventListener('pointermove', function (e) {
        if (!self.dragging || e.pointerId !== pid) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!axis) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axis === 'x') {
            self.track.classList.add('is-dragging');
            try { self.viewport.setPointerCapture(pid); } catch (_) {}
          }
        }
        if (axis !== 'x') return;
        var now = performance.now();
        velocity = (e.clientX - lastX) / Math.max(1, now - lastT);
        lastX = e.clientX;
        lastT = now;
        var max = self.snaps[self.snaps.length - 1] || 0;
        var x = startOffset - dx;
        if (!self.loop) {
          if (x < 0) x = x / 3;
          if (x > max) x = max + (x - max) / 3;
        }
        self.track.style.transform = 'translate3d(' + (-x) + 'px,0,0)';
      });

      function end(e) {
        if (!self.dragging || (e && e.pointerId !== pid)) return;
        self.dragging = false;
        self.track.classList.remove('is-dragging');
        if (axis !== 'x') return;
        var dx = lastX - startX;
        var target = startOffset - dx - velocity * 180;
        var best = 0, bestDist = Infinity;
        self.snaps.forEach(function (s, i) {
          var d = Math.abs(s - target);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        if (best === self.index && Math.abs(dx) > 40) best += dx < 0 ? 1 : -1;
        self.goTo(best);
        self.apply(true);
        self.restart();
        // Swallow the click a mouse drag fires on release, so dragging across
        // a slide doesn't also activate it. Touch drags don't fire one, so the
        // guard expires rather than eating a later, intentional click.
        if (Math.abs(dx) > 6) {
          var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
          self.viewport.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(function () { self.viewport.removeEventListener('click', swallow, { capture: true }); }, 300);
        }
      }

      this.viewport.addEventListener('pointerup', end);
      this.viewport.addEventListener('pointercancel', end);
      // A press released outside the viewport (no capture yet) must not leave
      // the slider thinking it's mid-drag — that would freeze autoplay.
      window.addEventListener('pointerup', function (e) {
        if (self.dragging && e.pointerId === pid && axis !== 'x') self.dragging = false;
      });
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-cycle — GIF-style image rotation: one [data-cycle-item] visible at a
     time, hard cut every data-interval ms. Holds the first image for
     reduced-motion users and pauses while scrolled out of view.
     ──────────────────────────────────────────────────────────────────── */
  define('gl-cycle', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var self = this;
      this.items = Array.from(this.querySelectorAll('[data-cycle-item]'));
      this.index = 0;
      this.interval = parseInt(this.dataset.interval || '1200', 10);
      if (this.items.length < 2 || REDUCED.matches) return;
      this.classList.add('is-cycling');
      this.show(0);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) self.start(); else self.stop();
        });
        this.io.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      this._init = false;
    }

    show(i) {
      this.index = i;
      this.items.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
    }

    start() {
      var self = this;
      this.stop();
      this.timer = setInterval(function () { self.show((self.index + 1) % self.items.length); }, this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-fit-row — keeps every [data-fit] element inside within its own box at
     a size shared with its group. data-fit="name" groups elements; for each
     group, if any member overflows, the factor --gl-fit-<name> steps down for
     the whole group until none does (floor 0.4). An empty data-fit uses
     --gl-fit. CSS multiplies font sizes by these factors.

     A member overflows when its text is wider than it (scrollWidth >
     clientWidth), when it runs past data-fit-lines="n" lines, or — with
     data-fit-height — when its content is taller than its box. A group also
     shrinks while its members' line counts differ by more than
     data-fit-spread="n" (set on any member), so wrapped blocks stay even.

     Members marked data-fit-match are then scaled up (--gl-match, set on
     each one) until their text is as wide as the group's widest text, so
     one-line headlines of different lengths end flush.
     ──────────────────────────────────────────────────────────────────── */
  function textWidth(el) {
    var range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width;
  }

  function overflows(el) {
    if (el.scrollWidth > el.clientWidth + 1) return true;
    var lines = parseInt(el.getAttribute('data-fit-lines') || '0', 10);
    if (lines) {
      var cs = getComputedStyle(el);
      var lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2;
      // offsetHeight is layout size, unaffected by transforms (the value-prop
      // collage is scaled on desktop).
      if (el.offsetHeight > lines * lh + 1) return true;
    }
    if (el.hasAttribute('data-fit-height') && el.scrollHeight > el.clientHeight + 1) return true;
    return false;
  }

  function lineCount(el) {
    var cs = getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2;
    return Math.round(el.offsetHeight / lh);
  }

  function spreadTooWide(items) {
    var limit = NaN;
    items.forEach(function (el) {
      var v = parseInt(el.getAttribute('data-fit-spread'), 10);
      if (!isNaN(v)) limit = isNaN(limit) ? v : Math.min(limit, v);
    });
    if (isNaN(limit) || items.length < 2) return false;
    var counts = items.map(lineCount);
    return Math.max.apply(null, counts) - Math.min.apply(null, counts) > limit;
  }

  define('gl-fit-row', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var self = this;
      this.fit = this.fit.bind(this);
      this.fit();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fit);
      if ('ResizeObserver' in window) {
        this._lastWidth = this.clientWidth;
        this.ro = new ResizeObserver(function () {
          if (self.clientWidth === self._lastWidth) return;
          self._lastWidth = self.clientWidth;
          self.fit();
        });
        this.ro.observe(this);
      } else {
        window.addEventListener('resize', debounce(this.fit, 120));
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      this._init = false;
    }

    fit() {
      var groups = {};
      this.querySelectorAll('[data-fit]').forEach(function (el) {
        var name = el.getAttribute('data-fit');
        (groups[name] = groups[name] || []).push(el);
      });
      var self = this;
      Object.keys(groups).forEach(function (name) {
        var prop = name ? '--gl-fit-' + name : '--gl-fit';
        var items = groups[name];
        var scale = 1;
        var matched = items.filter(function (el) { return el.hasAttribute('data-fit-match'); });
        matched.forEach(function (el) { el.style.setProperty('--gl-match', '1'); });
        self.style.setProperty(prop, '1');
        for (var i = 0; i < 16; i++) {
          var overflowing = items.some(overflows) || spreadTooWide(items);
          if (!overflowing) break;
          scale = Math.max(0.4, scale - 0.05);
          self.style.setProperty(prop, String(scale));
          if (scale === 0.4) break;
        }
        if (!matched.length) return;
        var widths = matched.map(textWidth);
        var widest = Math.max.apply(null, widths);
        matched.forEach(function (el, i) {
          if (widths[i] > 0) el.style.setProperty('--gl-match', String(widest / widths[i]));
        });
      });
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-accordion — any [data-gl-acc-trigger] toggles aria-expanded and the
     row's .is-open class; the panel's height animates in CSS
     (grid-template-rows 0fr → 1fr).
     ──────────────────────────────────────────────────────────────────── */
  define('gl-accordion', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-gl-acc-trigger]');
        if (!trigger || !this.contains(trigger)) return;
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        var row = trigger.closest('.gl-acc');
        if (row) row.classList.toggle('is-open', !open);
      });
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-gallery — main gl-slider + thumbnail rail. Thumbs drive the slider,
     the slider marks the current thumb. [data-nutrition-open] anywhere in
     the element opens the nutrition <dialog>.
     ──────────────────────────────────────────────────────────────────── */
  define('gl-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var self = this;
      this.slider = this.querySelector('gl-slider');
      this.thumbs = Array.from(this.querySelectorAll('[data-gl-thumb]'));
      this.dialog = this.closest('[data-gl-buybox]')
        ? this.closest('[data-gl-buybox]').querySelector('[data-nutrition-modal]')
        : this.querySelector('[data-nutrition-modal]');

      this.thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { if (self.slider) self.slider.goTo(i); });
      });
      if (this.slider) {
        this.slider.addEventListener('gl:select', function (e) { self.mark(e.detail.index); });
      }

      var root = this.closest('[data-gl-buybox]') || this;
      root.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-nutrition-open]');
        if (opener && self.dialog && typeof self.dialog.showModal === 'function') {
          // A non-empty value names which label to show (dialogs holding
          // more than one label switch on data-show).
          var which = opener.getAttribute('data-nutrition-open');
          if (which) self.dialog.setAttribute('data-show', which);
          self.dialog.showModal();
        }
        if (e.target.closest('[data-nutrition-close]') && self.dialog) self.dialog.close();
      });
      root.addEventListener('keydown', function (e) {
        var opener = e.target.closest('[data-nutrition-open][role="button"]');
        if (opener && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          if (!self.dialog) return;
          var which = opener.getAttribute('data-nutrition-open');
          if (which) self.dialog.setAttribute('data-show', which);
          self.dialog.showModal();
        }
      });
      if (this.dialog) {
        this.dialog.addEventListener('click', function (e) {
          if (e.target === self.dialog) self.dialog.close();
        });
      }
    }

    mark(i) {
      this.thumbs.forEach(function (t, n) {
        t.setAttribute('aria-current', String(n === i));
      });
      var t = this.thumbs[i];
      var rail = t && t.parentElement;
      if (rail && rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: t.offsetLeft - rail.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
      }
    }
  });

  /* ────────────────────────────────────────────────────────────────────
     gl-buybox — the homepage's standalone buy box (sections/gl-buybox.liquid).
     Two options (milk / plain, each one fixed variant) × two purchase types
     (subscription / one-time). Reads the JSON config the section renders,
     writes the copy for the selected combination, jumps the gallery to the
     matching image, and adds the variant (+ selling plan) to the cart, then
     refreshes and opens the theme's cart drawer.
     ──────────────────────────────────────────────────────────────────── */
  define('gl-buybox', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var self = this;
      try {
        this.config = JSON.parse(this.querySelector('[data-gl-buybox-config]').textContent);
      } catch (_) {
        return;
      }
      this.form = this.querySelector('[data-buybox-form]');
      this.cta = this.querySelector('[data-add-to-cart]');
      this.error = this.querySelector('[data-form-error]');
      this.renewal = this.querySelector('[data-renewal-copy]');
      this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.config.currency || 'USD' });

      this.querySelectorAll('[data-flavor-option], [data-purchase-option]').forEach(function (input) {
        input.addEventListener('change', function () { self.render(true); });
      });
      if (this.form) {
        this.form.addEventListener('submit', function (e) {
          e.preventDefault();
          self.addToCart();
        });
      }
      this.render(false);
    }

    get flavor() {
      var el = this.querySelector('[data-flavor-option]:checked');
      return el ? el.value : 'milk';
    }

    get plan() {
      var el = this.querySelector('[data-purchase-option]:checked');
      return el ? el.value : 'subscription';
    }

    format(cents) {
      return this.money.format((Number(cents) || 0) / 100);
    }

    perServing(cents, servings) {
      return this.format(Math.round((Number(cents) || 0) / Math.max(1, servings))) + (this.dataset.suffix || '/cup');
    }

    render(userAction) {
      var f = this.config.flavors[this.flavor] || {};
      var hasPlan = !!f.sellingPlanId;

      // A product without a selling plan can't be subscribed to: fall back to
      // one-time and disable the subscription card.
      var subInput = this.querySelector('[data-purchase-option][value="subscription"]');
      var subCard = this.querySelector('[data-purchase-card="subscription"]');
      if (subInput) {
        subInput.disabled = !hasPlan;
        if (subCard) subCard.classList.toggle('is-disabled', !hasPlan);
        if (!hasPlan && subInput.checked) {
          var once = this.querySelector('[data-purchase-option][value="one-time"]');
          if (once) once.checked = true;
        }
      }

      var flavor = this.flavor;
      var plan = this.plan;
      this.setAttribute('data-flavor', flavor);
      this.setAttribute('data-plan', plan);

      var servings = f.servings || 1;
      var self = this;
      this.querySelectorAll('[data-pack-copy]').forEach(function (el) {
        el.textContent = (el.getAttribute('data-template') || '').replace(/\{servings\}/g, servings);
      });

      this.setText('[data-price="subscription"]', hasPlan ? this.format(f.subFirst) : '—');
      this.setText('[data-compare-price="subscription"]', hasPlan && f.subFirst < f.once ? this.format(f.once) : '');
      this.setText('[data-per-serving="subscription"]', hasPlan ? this.perServing(f.subFirst, servings) : '—');
      this.setText('[data-price="one-time"]', this.format(f.once));
      this.setText('[data-per-serving="one-time"]', this.perServing(f.once, servings));

      if (this.renewal) {
        var showRenewal = plan === 'subscription' && hasPlan;
        this.renewal.hidden = !showRenewal;
        this.renewal.textContent = showRenewal
          ? (this.renewal.getAttribute('data-template') || '').replace(/\{price\}/g, this.format(f.subRecurring))
          : '';
      }

      var link = this.querySelector('[data-nutrition-link]');
      if (link) link.setAttribute('data-nutrition-open', flavor);

      if (this.cta) {
        var soldOut = !f.available || !f.variantId;
        this.cta.disabled = soldOut;
        this.cta.textContent = soldOut
          ? this.cta.getAttribute('data-label-sold-out')
          : this.cta.getAttribute(plan === 'subscription' ? 'data-label-subscription' : 'data-label-one-time');
      }
      if (this.error) this.error.hidden = true;

      // Jump the gallery to the image tagged for this combination.
      var slides = Array.from(this.querySelectorAll('[data-gallery-role]'));
      var target = slides.findIndex(function (s) { return s.getAttribute('data-gallery-role') === flavor + '-' + plan; });
      var slider = this.querySelector('.gl-gallery gl-slider');
      if (target > -1 && slider && slider.goTo) {
        if (userAction) slider.goTo(target);
        else {
          slider.index = target;
          if (slider.apply) slider.apply(false);
          slider.dispatchEvent(new CustomEvent('gl:select', { detail: { index: target } }));
        }
      }
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach(function (el) { el.textContent = text; });
    }

    addToCart() {
      var self = this;
      var f = this.config.flavors[this.flavor] || {};
      if (!f.variantId || this.busy) return;
      var item = { id: f.variantId, quantity: 1 };
      var subscribing = this.plan === 'subscription' && !!f.sellingPlanId;
      if (subscribing) item.selling_plan = f.sellingPlanId;
      var gift = this.config.gift;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.busy = true;
      if (this.cta) this.cta.classList.add('is-busy');

      // Subscriptions earn the free Travel Pack. Add it in the same request
      // as the coffee (so the drawer opens with it already there) unless the
      // cart already has one or the shopper removed it from this cart. The
      // cart-sync in layout/theme.liquid is the safety net either way.
      var withGift = subscribing && gift
        ? fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var hasGift = (cart.items || []).some(function (l) { return l.product_id === gift.productId; });
              var declined = cart.attributes && cart.attributes._gift_declined;
              return !hasGift && !declined;
            })
            .catch(function () { return false; })
        : Promise.resolve(false);

      withGift.then(function (addGift) {
        var items = [item];
        if (addGift) items.push({ id: gift.variantId, quantity: 1, properties: { _gift: 'subscription' } });
        var body = { items: items };
        var mini = document.querySelector('xo-cart-mini');
        if (mini && mini.id) {
          body.sections = mini.id;
          body.sections_url = window.location.pathname;
        }
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');
          return data;
        });
      }).then(function (data) {
        if (typeof window.brisstaApplyCartChange === 'function') window.brisstaApplyCartChange(data);
        if (window.xoModal && window.xoModal.open) window.xoModal.open('cart');
        else window.location.href = root + 'cart';
      }).catch(function (err) {
        if (self.error) {
          self.error.textContent = err.message || 'Unable to add this item.';
          self.error.hidden = false;
        }
      }).then(function () {
        self.busy = false;
        if (self.cta) self.cta.classList.remove('is-busy');
      });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { deferFrames(document); });
  } else {
    deferFrames(document);
  }
})();
